<div class="header">
  <p-toolbar styleClass="p-0 bg-white border-noround px-2 toolbar-nav">
    <div class="w-7 p-toolbar-group-left flex">
      <img src="./assets/jax-logo.png" alt="JAX Logo" class="mx-2 mt-1" />
      <h2 class="font-semibold text-indigo-800">
        {{ title }} {{ titleSuffix }}
      </h2>
    </div>

    <div class="align-items-right px-4">
      <ng-container *ngIf="user$ | async as user">
        <i class="pi pi-user"></i>

        <span class="font-medium pl-2">{{ user.name }} <{{ user.email }}></span>
      </ng-container>
    </div>

    <div class="p-toolbar-group-right">

      <div *ngIf="(auth.isAuthenticated$ | async) === false; then loginSection; else userMenu"></div>

      <ng-template #loginSection>
        <button
          pButton
          type="button"
          id="login-button"
          class="p-button-text"
          label="Sign In"
          icon="pi pi-sign-in"
          (click)="login()"
        ></button>
      </ng-template>

      <ng-template #userMenu>
				<button
				  pButton
				  type="button"
				  id="jit-shell-button"
				  class="p-button-text p-button-plain"
				  label="Get JIT Shell"
					icon="pi pi-arrow-circle-down"
					(click)="showDialog = true"
				></button>
        <button
          pButton
          type="button"
          id="logout-button"
          class="p-button-text p-button-plain"
          label="Sign Out"
          icon="pi pi-sign-out"
          (click)="logout()"
        ></button>
      </ng-template>
    </div>
  </p-toolbar>
</div>

<p-dialog [(visible)]="showDialog" [style]="{width: '800px'}" [modal]="true">

	<div class="w-9 p-toolbar-group-left flex">
    <img src="./assets/jax-logo.png" alt="JAX Logo" class="mx-5 mt-3" />
    <h3>JAX Image Tools Shell</h3>
  </div>

	<div style="max-height: 70vh; overflow-y: auto;">

	An interactive shell that lets you do everything the web application does from the
	<i>comfort</i> of your terminal - upload images, run analyses, watch a directory and
	process new files automatically, and (if you've been granted rights to) manage who can
	see, edit, or delete a bucket's contents. Here is how to get started:<br/>
	<ol>
	  <li>Install <a href="https://docs.aws.amazon.com/corretto/latest/corretto-21-ug/downloads-list.html" target="_blank">Amazon Corretto</a>
		if you don't have Java installed.<br/>
		<small> (Note: If you do not have rights to install,
			use the zip version and add the java command to your path.) </small>
		</li>
	  <br/>
		<!-- TODO The jar file should not be here. Instead we should release JIT Shell on maven
			central and link to it from here. Other parts of the CIMG source code should also
			be released there when we commit to open source mode. -->
		<li>Download the <a href="assets/jit-shell.jar" target="_blank" 
			rel="noopener noreferrer">JIT Shell</a> jar</li>
		<br/>
		<li>In a terminal or cmd window run 'java -jar FILE_DOWNLOADED'
					e.g. 'java -jar ~/Downloads/jit-shell.jar'. <br/>
					<small>Use the 'version' command to check version.</small></li>
		<br/>
		<li>Log in with <code>login</code> - this opens your browser to sign in, and the
			shell remembers you (and where you left off) between sessions, so you don't need
			to log in again every time you restart it.</li>
	</ol>

	<h5>Selecting a project and file</h5>
	Most commands work on whatever project/file you have currently selected.
	<ol>
		<li><code>projects</code> - list every project (bucket) you can see.</li>
		<li><code>project</code> - pick one of those projects to work in.</li>
		<li><code>list</code> - show the contents of the current folder in the selected project.</li>
		<li><code>select</code> - pick a file or folder from that listing; run <code>select</code>
			again with no argument to browse deeper, or <code>select ..</code> to go back up a level.</li>
	</ol>

	<h5>Running and monitoring a process</h5>
	<ol>
		<li><code>process</code> - run an analysis on the currently selected file. This walks
			you through choosing the tool and its parameters, the same options the web
			application's process dialog offers.</li>
		<li><code>status</code> - check on a workflow by id, or see your last ten if you don't
			have one handy.</li>
		<li><code>monitor</code> - watch a running workflow's progress live until it finishes.</li>
		<li><code>results</code> - list the results of your last completed process.</li>
		<li><code>download</code> - download one of those result files to your machine.</li>
	</ol>
	Also see <code>upload</code> (send local files to storage) and <code>watch</code>
	(watch a local directory and automatically upload and process new files as they appear -
	handy for an instrument that drops files into a folder as it scans).

	<h5>Managing who can see, edit, or delete a bucket (lab/microscopy admins)</h5>
	If you've been granted permission-admin rights for JIT (ask your JIT/FGA administrator -
	this isn't something you can grant yourself), you can manage a bucket's users directly
	from the shell instead of asking an administrator to do it for you:
	<ol>
		<li><code>permission get &lt;email&gt;</code> - show what that person can currently do
			across every bucket.</li>
		<li><code>permission get &lt;email&gt; &lt;bucket&gt;</code> - check just one bucket.</li>
		<li><code>permission set &lt;email&gt; &lt;bucket&gt; &lt;READ|EDIT|ALL&gt;</code> - grant
			view-only (<code>READ</code>), view-and-edit (<code>EDIT</code>), or full control
			(<code>ALL</code>) access.</li>
		<li><code>permission remove &lt;email&gt; &lt;bucket&gt;</code> - revoke their access to
			that bucket entirely.</li>
	</ol>
	If these commands don't work for you, you likely haven't been granted permission-admin
	rights yet - that's expected, not a bug.
	<br/><br/>
	For example, checking one person's access across every bucket looks like this:
	<pre>
	Jax Image Tools&gt;permission get someone&#64;jax.org
	Permissions for someone&#64;jax.org:
	Bucket                   View  Edit  Delete  Level
	jax-cimg-sample-data     Yes   No    No      READ
	jax-your-lab             Yes   Yes   Yes     ALL
	jax-another-lab          No    No    No      NONE
	</pre>
	<code>View</code>/<code>Edit</code>/<code>Delete</code> are Yes/No for that specific
	capability; <code>Level</code> is the plain-English summary (<code>NONE</code>,
	<code>READ</code>, <code>EDIT</code>, or <code>ALL</code>). A warning under the table
	saying a bucket is "public" just means it's a shared bucket (like sample data) visible
	to everyone - there's nothing to configure for those. A warning about "no Auth0 account
	found" is normal for anyone who hasn't logged into JIT yet - the grant still takes effect
	automatically the first time they do.

	<h5>Tips:</h5>
	<ol>
		<li>Run <code>help</code> at any time to see every available command, and check a
				command's own help for its shorter aliases.</li>
		<li>JIT Shell saves what you were doing between sessions and when it
				restarts, it will pick up where you left off.</li>
	</ol>

	</div>
</p-dialog>
